import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GET, POST, PUT, DELETE } from '../../configs/api';
import { api } from '../../utils/constants';

export const fetchAlertsMeta = createAsyncThunk(
  'alerts/fetchMeta',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error, message } = await GET(api.getAlertsMeta);
      if (error) return rejectWithValue(message || 'Failed to load alert options');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load alert options');
    }
  },
);

export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error, message } = await GET(api.getAlerts);
      if (error) return rejectWithValue(message || 'Failed to load alerts');
      return data?.alerts || [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load alerts');
    }
  },
);

export const createAlert = createAsyncThunk(
  'alerts/createAlert',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const { data, error, message } = await POST(api.createAlert, {}, body);
      if (error) return rejectWithValue(message || 'Failed to create alert');
      dispatch(fetchAlerts());
      return data?.alert;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create alert');
    }
  },
);

export const updateAlert = createAsyncThunk(
  'alerts/updateAlert',
  async ({ id, body }, { rejectWithValue, dispatch }) => {
    try {
      const { data, error, message } = await PUT(`${api.updateAlert}/${id}`, {}, body);
      if (error) return rejectWithValue(message || 'Failed to update alert');
      dispatch(fetchAlerts());
      return data?.alert;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update alert');
    }
  },
);

export const deleteAlert = createAsyncThunk(
  'alerts/deleteAlert',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { error, message } = await DELETE(`${api.deleteAlert}/${id}`);
      if (error) return rejectWithValue(message || 'Failed to delete alert');
      dispatch(fetchAlerts());
      return id;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete alert');
    }
  },
);

export const testAlert = createAsyncThunk(
  'alerts/testAlert',
  async (id, { rejectWithValue }) => {
    try {
      const { data, error, message } = await POST(`${api.testAlert}/${id}/test`);
      if (error) return rejectWithValue(message || 'Failed to send test');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to send test');
    }
  },
);

export const fetchAlertGroups = createAsyncThunk(
  'alerts/fetchGroups',
  async (_, { rejectWithValue }) => {
    try {
      const { data, error, message } = await GET(api.getAlertGroups);
      if (error) return rejectWithValue(message || 'Failed to load groups');
      return data?.groups || [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load groups');
    }
  },
);

export const createAlertGroup = createAsyncThunk(
  'alerts/createGroup',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const { data, error, message } = await POST(api.createAlertGroup, {}, body);
      if (error) return rejectWithValue(message || 'Failed to create group');
      dispatch(fetchAlertGroups());
      return data?.group;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create group');
    }
  },
);

export const updateAlertGroup = createAsyncThunk(
  'alerts/updateGroup',
  async ({ id, body }, { rejectWithValue, dispatch }) => {
    try {
      const { data, error, message } = await PUT(`${api.updateAlertGroup}/${id}`, {}, body);
      if (error) return rejectWithValue(message || 'Failed to update group');
      dispatch(fetchAlertGroups());
      return data?.group;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update group');
    }
  },
);

export const deleteAlertGroup = createAsyncThunk(
  'alerts/deleteGroup',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const { error, message } = await DELETE(`${api.deleteAlertGroup}/${id}`);
      if (error) return rejectWithValue(message || 'Failed to delete group');
      dispatch(fetchAlertGroups());
      return id;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete group');
    }
  },
);

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: {
    meta: null,
    alerts: [],
    groups: [],
    loading: false,
    groupsLoading: false,
    error: null,
  },
  reducers: {
    clearAlertsError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlertsMeta.fulfilled, (state, action) => {
        state.meta = action.payload;
      })
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load alerts';
      })
      .addCase(fetchAlertGroups.pending, (state) => {
        state.groupsLoading = true;
      })
      .addCase(fetchAlertGroups.fulfilled, (state, action) => {
        state.groupsLoading = false;
        state.groups = action.payload;
      })
      .addCase(fetchAlertGroups.rejected, (state, action) => {
        state.groupsLoading = false;
        state.error = action.payload || 'Failed to load groups';
      });
  },
});

export const { clearAlertsError } = alertsSlice.actions;
export const selectAlerts = (state) => state.alerts.alerts;
export const selectAlertGroups = (state) => state.alerts.groups;
export const selectAlertsMeta = (state) => state.alerts.meta;
export const selectAlertsLoading = (state) => state.alerts.loading;
export default alertsSlice.reducer;
